/**
 * Etiket tasarımcısı özellik paneli.
 *
 * İki sekme: "Etiket" (fiziksel ölçü, yön, çözünürlük, sayfa yerleşimi) ve
 * "Eleman" (seçili elemanın özellikleri). Tüm ölçüler mm.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  BarcodeFormat,
  DPI_OPTIONS,
  LabelConditionOperator,
  LabelElement,
  LabelRotation,
  LabelTemplate,
  PrinterLanguage,
  fitSheetLayout,
  createRollLayout,
  getConditionFields,
  getDesignSize,
  getPlaceholders,
} from '../../../types/labelTemplate.types';
import { validateBarcodeValue } from '../utils/barcodeRenderer';
import {
  DOMESTIC_LOGO_ASPECT,
  DOMESTIC_LOGO_TOKEN,
  isDomesticLogo,
  resolveImageSrc,
} from '../assets/domesticBadge';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';
import { useAuth } from '../../../contexts/AuthContext';

export interface LabelPropertiesPanelProps {
  template: LabelTemplate;
  onTemplateChange: (patch: Partial<LabelTemplate>) => void;
  selectedElement: LabelElement | null;
  onElementChange: (patch: Partial<LabelElement>) => void;
  onDeleteElement: () => void;
  onDuplicateElement: () => void;
}

const FONT_FAMILIES = ['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana', 'Tahoma'];

const BARCODE_FORMATS: BarcodeFormat[] = ['CODE128', 'EAN13', 'EAN8', 'CODE39', 'ITF14'];

/** mm girişleri için ondalık kabul eden sayı alanı */
const NumField: React.FC<{
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  suffix?: string;
  disabled?: boolean;
}> = ({ label, value, onChange, step = 0.5, min = 0, suffix = 'mm', disabled }) => (
  <TextField
    label={label}
    type="number"
    size="small"
    fullWidth
    disabled={disabled}
    value={value ?? ''}
    onChange={(e) => {
      const n = parseFloat(e.target.value);
      if (!Number.isNaN(n)) onChange(n);
    }}
    inputProps={{ step, min }}
    InputProps={{ endAdornment: suffix ? <Typography variant="caption" color="text.secondary">{suffix}</Typography> : undefined }}
  />
);

const LabelPropertiesPanel: React.FC<LabelPropertiesPanelProps> = ({
  template,
  onTemplateChange,
  selectedElement,
  onElementChange,
  onDeleteElement,
  onDuplicateElement,
}) => {
  const [tab, setTab] = useState(0);
  const [imageLibOpen, setImageLibOpen] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId ?? null;
  const el = selectedElement;
  const design = getDesignSize(template);
  const placeholders = useMemo(() => getPlaceholders(template.dataSource), [template.dataSource]);
  const conditionFields = useMemo(() => getConditionFields(template.dataSource), [template.dataSource]);

  // Eleman seçildiğinde otomatik olarak eleman sekmesine geç
  React.useEffect(() => {
    if (el) setTab(1);
  }, [el?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const insertPlaceholder = (token: string) => {
    if (!el) return;
    if (el.type === 'barcode') {
      onElementChange({ barcodeValue: (el.barcodeValue ?? '') + token });
    } else if (el.type === 'qr') {
      onElementChange({ qrValue: (el.qrValue ?? '') + token });
    } else {
      onElementChange({ text: (el.text ?? '') + token });
    }
  };

  /**
   * Görseli elemana uygular ve yüksekliği resmin gerçek oranına göre düzeltir.
   * Oran bilinmeden atanırsa görsel kutuya sığdırılıp eziliyor; kütüphane
   * resminin ölçüsü önceden bilinmediği için tarayıcıya yükletip ölçüyoruz.
   */
  const applyImageSrc = useCallback(
    (src: string, knownAspect?: number) => {
      if (!el) return;

      const setWithAspect = (aspect: number) => {
        if (!Number.isFinite(aspect) || aspect <= 0) {
          onElementChange({ src });
          return;
        }
        onElementChange({ src, heightMm: Number((el.widthMm / aspect).toFixed(2)) });
      };

      if (knownAspect) {
        setWithAspect(knownAspect);
        return;
      }

      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => setWithAspect(probe.naturalWidth / probe.naturalHeight);
      // Ölçü alınamazsa görseli yine ata; kullanıcı boyutu elle düzeltebilir
      probe.onerror = () => onElementChange({ src });
      probe.src = resolveImageSrc(src);
    },
    [el, onElementChange],
  );

  const applyDomesticLogo = useCallback(() => {
    setImageError(null);
    applyImageSrc(DOMESTIC_LOGO_TOKEN, DOMESTIC_LOGO_ASPECT);
  }, [applyImageSrc]);

  /** Kütüphane video da listeliyor; etikete video basılamaz */
  const handleLibrarySelect = useCallback(
    (urls: string[]) => {
      setImageLibOpen(false);
      const url = urls[0];
      if (!url) return;

      if (/\.(mp4|webm|mov|avi)(\?|$)/i.test(url)) {
        setImageError('Etikete video eklenemez, bir resim seçin.');
        return;
      }

      setImageError(null);
      applyImageSrc(url);
    },
    [applyImageSrc],
  );

  const barcodeError =
    el?.type === 'barcode' && el.barcodeValue && !el.barcodeValue.includes('{{')
      ? validateBarcodeValue(el.barcodeValue, el.barcodeFormat ?? 'CODE128')
      : null;

  return (
    <Box sx={{ width: 320, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
        <Tab label="Etiket" />
        <Tab label="Eleman" disabled={!el} />
      </Tabs>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {tab === 0 && (
          <Stack spacing={2}>
            <TextField
              label="Şablon Adı"
              size="small"
              fullWidth
              value={template.name}
              onChange={(e) => onTemplateChange({ name: e.target.value })}
            />

            <Divider textAlign="left">
              <Typography variant="caption">Fiziksel Etiket</Typography>
            </Divider>

            <Typography variant="caption" color="text.secondary">
              Rulodaki etiketin gerçek ölçüsü. Genişlik besleme yönüne diktir.
            </Typography>

            <Stack direction="row" spacing={1}>
              <NumField
                label="Genişlik"
                value={template.labelWidthMm}
                onChange={(v) => onTemplateChange({ labelWidthMm: v })}
                min={5}
              />
              <NumField
                label="Uzunluk"
                value={template.labelLengthMm}
                onChange={(v) => onTemplateChange({ labelLengthMm: v })}
                min={5}
              />
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Tasarım Yönü
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={template.rotation}
                onChange={(_, v) => v !== null && onTemplateChange({ rotation: v as LabelRotation })}
              >
                <ToggleButton value={0}>0°</ToggleButton>
                <ToggleButton value={90}>90°</ToggleButton>
                <ToggleButton value={180}>180°</ToggleButton>
                <ToggleButton value={270}>270°</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Tasarım tuvali: {design.widthMm} × {design.heightMm} mm
                {template.rotation !== 0 && ' (döndürülerek basılır)'}
              </Typography>
            </Box>

            <Divider textAlign="left">
              <Typography variant="caption">Çıktı</Typography>
            </Divider>

            <TextField
              label="Yazıcı Dili"
              select
              size="small"
              fullWidth
              value={template.printerLanguage}
              onChange={(e) => onTemplateChange({ printerLanguage: e.target.value as PrinterLanguage })}
              helperText={
                template.printerLanguage === 'BROWSER'
                  ? 'Tarayıcı/PDF üzerinden — her yazıcıda çalışır'
                  : 'Etiket yazıcısına ham komut — sessiz yazdırma'
              }
            >
              <MenuItem value="BROWSER">Tarayıcı / PDF</MenuItem>
              <MenuItem value="PPLA">PPLA (Argox)</MenuItem>
              <MenuItem value="PPLB">PPLB (Argox / TSC)</MenuItem>
              <MenuItem value="ZPL">ZPL (Zebra)</MenuItem>
              <MenuItem value="EPL">EPL (Zebra eski)</MenuItem>
            </TextField>

            <TextField
              label="Çözünürlük"
              select
              size="small"
              fullWidth
              value={template.dpi}
              onChange={(e) => onTemplateChange({ dpi: Number(e.target.value) })}
            >
              {DPI_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d} dpi
                </MenuItem>
              ))}
            </TextField>

            <Divider textAlign="left">
              <Typography variant="caption">Sayfa Yerleşimi</Typography>
            </Divider>

            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={template.layout.mode}
              onChange={(_, v) => {
                if (!v) return;
                onTemplateChange({
                  layout:
                    v === 'roll'
                      ? createRollLayout()
                      : fitSheetLayout(design.widthMm, design.heightMm),
                });
              }}
            >
              <ToggleButton value="roll">Rulo</ToggleButton>
              <ToggleButton value="sheet">A4 Sayfa</ToggleButton>
            </ToggleButtonGroup>

            {template.layout.mode === 'sheet' && (
              <>
                <Stack direction="row" spacing={1}>
                  <NumField
                    label="Kolon"
                    value={template.layout.columns}
                    onChange={(v) => onTemplateChange({ layout: { ...template.layout, columns: Math.max(1, Math.round(v)) } })}
                    step={1}
                    min={1}
                    suffix=""
                  />
                  <NumField
                    label="Satır"
                    value={template.layout.rows}
                    onChange={(v) => onTemplateChange({ layout: { ...template.layout, rows: Math.max(1, Math.round(v)) } })}
                    step={1}
                    min={1}
                    suffix=""
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <NumField
                    label="Yatay Boşluk"
                    value={template.layout.gapXMm}
                    onChange={(v) => onTemplateChange({ layout: { ...template.layout, gapXMm: v } })}
                  />
                  <NumField
                    label="Dikey Boşluk"
                    value={template.layout.gapYMm}
                    onChange={(v) => onTemplateChange({ layout: { ...template.layout, gapYMm: v } })}
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <NumField
                    label="Üst Kenar"
                    value={template.layout.marginTopMm}
                    onChange={(v) => onTemplateChange({ layout: { ...template.layout, marginTopMm: v } })}
                  />
                  <NumField
                    label="Sol Kenar"
                    value={template.layout.marginLeftMm}
                    onChange={(v) => onTemplateChange({ layout: { ...template.layout, marginLeftMm: v } })}
                  />
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onTemplateChange({ layout: fitSheetLayout(design.widthMm, design.heightMm) })}
                >
                  A4'e Otomatik Sığdır
                </Button>
              </>
            )}
          </Stack>
        )}

        {tab === 1 && el && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">
                {el.type === 'text' && 'Metin'}
                {el.type === 'barcode' && 'Barkod'}
                {el.type === 'qr' && 'QR Kod'}
                {el.type === 'image' && 'Görsel'}
                {el.type === 'rect' && 'Kutu'}
                {el.type === 'line' && 'Çizgi'}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={el.locked ? 'Kilidi aç' : 'Kilitle'}>
                  <IconButton size="small" onClick={() => onElementChange({ locked: !el.locked })}>
                    {el.locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Kopyala">
                  <IconButton size="small" onClick={onDuplicateElement}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Sil">
                  <IconButton size="small" color="error" onClick={onDeleteElement}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {/* --- İçerik --- */}
            {(el.type === 'text' || el.type === 'barcode' || el.type === 'qr') && (
              <>
                <TextField
                  label={el.type === 'text' ? 'Metin' : 'Değer'}
                  size="small"
                  fullWidth
                  multiline={el.type === 'text'}
                  minRows={el.type === 'text' ? 2 : 1}
                  value={el.type === 'barcode' ? el.barcodeValue ?? '' : el.type === 'qr' ? el.qrValue ?? '' : el.text ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (el.type === 'barcode') onElementChange({ barcodeValue: v });
                    else if (el.type === 'qr') onElementChange({ qrValue: v });
                    else onElementChange({ text: v });
                  }}
                  error={!!barcodeError}
                  helperText={barcodeError ?? undefined}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Veri alanı ekle
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 130, overflowY: 'auto' }}>
                    {placeholders.map((p) => (
                      <Button
                        key={p.key}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, py: 0.1, px: 0.7, textTransform: 'none', minWidth: 0 }}
                        onClick={() => insertPlaceholder(p.key)}
                      >
                        {p.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {el.type === 'image' && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoLibraryIcon />}
                  onClick={() => setImageLibOpen(true)}
                >
                  Kütüphaneden Seç
                </Button>

                {!isDomesticLogo(el.src) && (
                  <Button size="small" startIcon={<VerifiedIcon />} onClick={applyDomesticLogo}>
                    Yerli Üretim logosunu kullan
                  </Button>
                )}

                {imageError && (
                  <Typography variant="caption" color="error">
                    {imageError}
                  </Typography>
                )}
              </>
            )}

            <Divider textAlign="left">
              <Typography variant="caption">Konum ve Boyut</Typography>
            </Divider>

            <Stack direction="row" spacing={1}>
              <NumField label="X" value={el.xMm} onChange={(v) => onElementChange({ xMm: v })} step={0.5} min={-50} />
              <NumField label="Y" value={el.yMm} onChange={(v) => onElementChange({ yMm: v })} step={0.5} min={-50} />
            </Stack>
            <Stack direction="row" spacing={1}>
              <NumField label="Genişlik" value={el.widthMm} onChange={(v) => onElementChange({ widthMm: v })} min={0.5} />
              <NumField
                label={el.type === 'line' ? 'Kalınlık' : 'Yükseklik'}
                value={el.heightMm}
                onChange={(v) => onElementChange({ heightMm: v })}
                step={el.type === 'line' ? 0.1 : 0.5}
                min={0.1}
              />
            </Stack>
            <NumField
              label="Döndürme"
              value={el.rotation ?? 0}
              onChange={(v) => onElementChange({ rotation: v })}
              step={15}
              min={-360}
              suffix="°"
            />

            {/* --- Metin biçimi --- */}
            {el.type === 'text' && (
              <>
                <Divider textAlign="left">
                  <Typography variant="caption">Yazı Biçimi</Typography>
                </Divider>
                <Stack direction="row" spacing={1} alignItems="center">
                  <NumField
                    label="Yazı Yüksekliği"
                    value={el.fontSizeMm}
                    onChange={(v) => onElementChange({ fontSizeMm: v })}
                    step={0.5}
                    min={0.5}
                  />
                  <ToggleButtonGroup size="small">
                    <ToggleButton value="bold" selected={!!el.bold} onClick={() => onElementChange({ bold: !el.bold })}>
                      <FormatBoldIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="italic" selected={!!el.italic} onClick={() => onElementChange({ italic: !el.italic })}>
                      <FormatItalicIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                <TextField
                  label="Yazı Tipi"
                  select
                  size="small"
                  fullWidth
                  value={el.fontFamily ?? 'Arial'}
                  onChange={(e) => onElementChange({ fontFamily: e.target.value })}
                >
                  {FONT_FAMILIES.map((f) => (
                    <MenuItem key={f} value={f} sx={{ fontFamily: f }}>
                      {f}
                    </MenuItem>
                  ))}
                </TextField>

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  fullWidth
                  value={el.align ?? 'left'}
                  onChange={(_, v) => v && onElementChange({ align: v })}
                >
                  <ToggleButton value="left">
                    <FormatAlignLeftIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="center">
                    <FormatAlignCenterIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="right">
                    <FormatAlignRightIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Satır Aralığı: {el.lineHeight ?? 1.2}
                  </Typography>
                  <Slider
                    size="small"
                    min={0.8}
                    max={2.5}
                    step={0.05}
                    value={el.lineHeight ?? 1.2}
                    onChange={(_, v) => onElementChange({ lineHeight: v as number })}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={el.autoShrink ?? false}
                      onChange={(e) => onElementChange({ autoShrink: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">Sığmazsa punto küçült</Typography>}
                />
              </>
            )}

            {/* --- Barkod ayarları --- */}
            {el.type === 'barcode' && (
              <>
                <Divider textAlign="left">
                  <Typography variant="caption">Barkod Ayarları</Typography>
                </Divider>
                <TextField
                  label="Format"
                  select
                  size="small"
                  fullWidth
                  value={el.barcodeFormat ?? 'CODE128'}
                  onChange={(e) => onElementChange({ barcodeFormat: e.target.value as BarcodeFormat })}
                >
                  {BARCODE_FORMATS.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={el.showBarcodeText ?? true}
                      onChange={(e) => onElementChange({ showBarcodeText: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">Altında numarayı göster</Typography>}
                />
              </>
            )}

            {el.type === 'qr' && (
              <TextField
                label="Hata Düzeltme"
                select
                size="small"
                fullWidth
                value={el.qrErrorLevel ?? 'M'}
                onChange={(e) => onElementChange({ qrErrorLevel: e.target.value as 'L' | 'M' | 'Q' | 'H' })}
                helperText="Yüksek seviye hasara dayanıklı ama daha yoğun"
              >
                <MenuItem value="L">L — %7</MenuItem>
                <MenuItem value="M">M — %15</MenuItem>
                <MenuItem value="Q">Q — %25</MenuItem>
                <MenuItem value="H">H — %30</MenuItem>
              </TextField>
            )}

            {/* --- Renk --- */}
            <Divider textAlign="left">
              <Typography variant="caption">Renk</Typography>
            </Divider>

            {(el.type === 'text' || el.type === 'barcode' || el.type === 'qr') && (
              <TextField
                label="Renk"
                type="color"
                size="small"
                fullWidth
                value={el.color ?? '#000000'}
                onChange={(e) => onElementChange({ color: e.target.value })}
              />
            )}

            {(el.type === 'rect' || el.type === 'line') && (
              <>
                <TextField
                  label={el.type === 'line' ? 'Çizgi Rengi' : 'Dolgu Rengi'}
                  type="color"
                  size="small"
                  fullWidth
                  value={el.fillColor === 'transparent' ? '#ffffff' : el.fillColor ?? '#000000'}
                  onChange={(e) => onElementChange({ fillColor: e.target.value })}
                />
                {el.type === 'rect' && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={el.fillColor === 'transparent'}
                          onChange={(e) => onElementChange({ fillColor: e.target.checked ? 'transparent' : '#ffffff' })}
                        />
                      }
                      label={<Typography variant="body2">Dolgusuz (sadece çerçeve)</Typography>}
                    />
                    <TextField
                      label="Çerçeve Rengi"
                      type="color"
                      size="small"
                      fullWidth
                      value={el.strokeColor ?? '#000000'}
                      onChange={(e) => onElementChange({ strokeColor: e.target.value })}
                    />
                    <Stack direction="row" spacing={1}>
                      <NumField
                        label="Çerçeve"
                        value={el.strokeWidthMm}
                        onChange={(v) => onElementChange({ strokeWidthMm: v })}
                        step={0.1}
                      />
                      <NumField
                        label="Köşe Yuvarlama"
                        value={el.cornerRadiusMm}
                        onChange={(v) => onElementChange({ cornerRadiusMm: v })}
                        step={0.5}
                      />
                    </Stack>
                  </>
                )}
              </>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                Opaklık: {Math.round((el.opacity ?? 1) * 100)}%
              </Typography>
              <Slider
                size="small"
                min={0.1}
                max={1}
                step={0.05}
                value={el.opacity ?? 1}
                onChange={(_, v) => onElementChange({ opacity: v as number })}
              />
            </Box>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Görünme Koşulu
              </Typography>
            </Divider>

            <TextField
              select
              size="small"
              label="Alan"
              fullWidth
              value={el.condition?.placeholder ?? ''}
              onChange={(e) => {
                const placeholder = e.target.value;
                onElementChange({
                  condition: placeholder
                    ? { placeholder, operator: el.condition?.operator ?? 'isTrue', value: el.condition?.value }
                    : undefined,
                });
              }}
              helperText={
                el.condition
                  ? 'Koşul sağlanmazsa bu eleman basılmaz.'
                  : 'Her zaman basılır. Koşul eklemek için bir alan seçin.'
              }
            >
              <MenuItem value="">Koşul yok — her zaman bas</MenuItem>
              {conditionFields.map((p) => {
                const key = p.key.replace(/[{}]/g, '');
                return (
                  <MenuItem key={key} value={key}>
                    {p.label}
                  </MenuItem>
                );
              })}
            </TextField>

            {el.condition?.placeholder && (
              <>
                <TextField
                  select
                  size="small"
                  label="Kural"
                  fullWidth
                  value={el.condition.operator}
                  onChange={(e) =>
                    onElementChange({
                      condition: {
                        ...el.condition!,
                        operator: e.target.value as LabelConditionOperator,
                      },
                    })
                  }
                >
                  <MenuItem value="isTrue">Evet/işaretli ise</MenuItem>
                  <MenuItem value="isNotEmpty">Dolu ise</MenuItem>
                  <MenuItem value="isEmpty">Boş ise</MenuItem>
                  <MenuItem value="equals">Şuna eşitse</MenuItem>
                  <MenuItem value="notEquals">Şuna eşit değilse</MenuItem>
                </TextField>

                {(el.condition.operator === 'equals' || el.condition.operator === 'notEquals') && (
                  <TextField
                    size="small"
                    label="Değer"
                    fullWidth
                    value={el.condition.value ?? ''}
                    onChange={(e) =>
                      onElementChange({
                        condition: { ...el.condition!, value: e.target.value },
                      })
                    }
                  />
                )}
              </>
            )}
          </Stack>
        )}
      </Box>

      <ImageLibrarySelectionModal
        open={imageLibOpen}
        onClose={() => setImageLibOpen(false)}
        onSave={handleLibrarySelect}
        companyId={companyId}
        productName="Etiket Görseli"
        // Gömülü belirteç ve data URL'ler kütüphanede eşleşmez, aramaya sokmuyoruz
        currentImageUrls={
          el?.src && !el.src.startsWith('data:') && !el.src.startsWith('builtin:') ? [el.src] : []
        }
        productId={null}
        singleSelect
        noticeMessage="Etiket siyah-beyaz basılır. Yüksek kontrastlı, sade logolar seçin; fotoğraflar termal baskıda bozuk çıkar."
      />
    </Box>
  );
};

export default LabelPropertiesPanel;
